import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '../config/stripe';
import { createPaymentIntent } from '../services/paymentService';
import useSiteStatus from '../hooks/useSiteStatus';
import { formatBackDate } from '../services/siteStatusService';
import './CartPage.css';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Stripe Elements Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="payment-error">
          <h3>Payment system temporarily unavailable</h3>
          <p>Please try refreshing the page or contact support.</p>
          <button onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Stripe Elements Wrapper with loading handling
const StripeElementsWrapper = ({ product, onSuccess, onError }) => {
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(true);

  useEffect(() => {
    const setupStripe = async () => {
      try {
        // First create the payment intent regardless of Stripe.js loading
        console.log('Creating fresh payment intent...');
        const paymentData = await createPaymentIntent({
          amount: product.priceInCents,
          currency: product.currency,
          productId: product.id,
          productName: product.name,
          customerEmail: 'customer@example.com',
          timestamp: Date.now() // Add timestamp to ensure fresh request
        });

        console.log('Payment intent created for Elements:', paymentData);
        setClientSecret(paymentData.clientSecret);

        // Then try to load Stripe.js with timeout
        console.log('Loading Stripe.js...');
        const stripeLoadPromise = stripePromise;
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Stripe.js loading timeout')), 5000)
        );

        try {
          const stripe = await Promise.race([stripeLoadPromise, timeoutPromise]);
          if (stripe) {
            console.log('Stripe.js loaded successfully');
            setStripeLoaded(true);
          } else {
            throw new Error('Stripe.js returned null');
          }
        } catch (stripeError) {
          console.warn('Stripe.js loading issue:', stripeError.message);
          console.log('Continuing with limited functionality...');
          setStripeLoaded(true); // Still try to show the form
        }

        setPaymentLoading(false);
      } catch (error) {
        console.error('Setup error:', error);
        setStripeError(error.message);
        setPaymentLoading(false);
      }
    };

    if (product) {
      setupStripe();
    }
  }, [product]);

  // Only show error if we can't create payment intent at all
  if (stripeError && !clientSecret) {
    return (
      <div className="payment-error">
        <h3>Payment System Error</h3>
        <p>Unable to load payment system. This might be due to:</p>
        <ul>
          <li>Ad blockers blocking Stripe.js</li>
          <li>Network connectivity issues</li>
          <li>Browser security settings</li>
        </ul>
        <div className="error-actions">
          <button onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!stripeLoaded || paymentLoading || !clientSecret) {
    return (
      <div className="payment-loading">
        <div className="loading-spinner"></div>
        <p>Loading secure payment system...</p>
      </div>
    );
  }

  return (
    <div>
      {stripeError && (
        <div className="payment-warning">
          <p>⚠️ Browser security is blocking some Stripe features, but payment should still work.</p>
        </div>
      )}
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret: clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#ff69b4',
            }
          }
        }}
      >
        <SimplePaymentForm
          clientSecret={clientSecret}
          product={product}
          onSuccess={onSuccess}
          onError={onError}
        />
      </Elements>
    </div>
  );
};

// Payment Form - Using PaymentElement with Apple Pay support
const SimplePaymentForm = ({ clientSecret, product, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Customer information state
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      line1: '',
      city: '',
      postal_code: '',
      country: 'GB'
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setCustomerInfo(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setCustomerInfo(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!customerInfo.firstName.trim()) {
      setMessage('First name is required.');
      return false;
    }
    if (!customerInfo.lastName.trim()) {
      setMessage('Last name is required.');
      return false;
    }
    if (!customerInfo.email.trim() || !customerInfo.email.includes('@')) {
      setMessage('Valid email is required.');
      return false;
    }
    if (!customerInfo.phone.trim()) {
      setMessage('Phone number is required.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setMessage('Payment system not ready. Please wait...');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      console.log('Submitting payment with customer info:', customerInfo);

      // Confirm payment with PaymentElement (supports Apple Pay, Google Pay, cards, etc.)
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/success`,
          payment_method_data: {
            billing_details: {
              name: `${customerInfo.firstName} ${customerInfo.lastName}`.trim(),
              email: customerInfo.email,
              phone: customerInfo.phone,
              address: {
                line1: customerInfo.address.line1,
                city: customerInfo.address.city,
                postal_code: customerInfo.address.postal_code,
                country: customerInfo.address.country,
              },
            },
          },
        },
        redirect: 'if_required', // Only redirect if necessary (e.g., 3D Secure)
      });

      if (error) {
        console.error('Payment failed:', error);
        setMessage(error.message || 'Payment failed. Please try again.');
        setIsLoading(false);
        onError && onError(error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded:', paymentIntent);
        setMessage('Payment successful! Redirecting...');

        // Send order confirmation emails
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
          const emailResponse = await fetch(`${backendUrl}/api/send-order-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              customerInfo: {
                name: `${customerInfo.firstName} ${customerInfo.lastName}`.trim(),
                email: customerInfo.email,
                phone: customerInfo.phone,
                address: customerInfo.address
              },
              product: {
                ...product,
                paymentIntentId: paymentIntent.id
              }
            }),
          });

          if (emailResponse.ok) {
            console.log('✅ Order confirmation emails sent');
          } else {
            console.warn('⚠️ Failed to send confirmation emails');
          }
        } catch (emailError) {
          console.error('Email sending error:', emailError);
          // Continue anyway - payment succeeded
        }

        setTimeout(() => {
          onSuccess && onSuccess({
            paymentIntentId: paymentIntent.id,
            status: paymentIntent.status,
            amount: paymentIntent.amount,
            customerInfo: {
              name: `${customerInfo.firstName} ${customerInfo.lastName}`.trim(),
              email: customerInfo.email,
              phone: customerInfo.phone
            }
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setMessage('An unexpected error occurred. Please try again.');
      setIsLoading(false);
      onError && onError(error);
    }
  };


  return (
    <div className="simple-payment-form">
      <form onSubmit={handleSubmit}>
        {/* Customer Information */}
        <div className="customer-info-section">
          <h3>Customer Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={customerInfo.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={customerInfo.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={customerInfo.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={customerInfo.phone}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>

        {/* Billing Address */}
        <div className="billing-address-section">
          <h3>Billing Address</h3>

          <div className="form-group">
            <label htmlFor="address.line1">Address Line 1</label>
            <input
              type="text"
              id="address.line1"
              name="address.line1"
              value={customerInfo.address.line1}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address.city">City</label>
              <input
                type="text"
                id="address.city"
                name="address.city"
                value={customerInfo.address.city}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="address.postal_code">Postcode</label>
              <input
                type="text"
                id="address.postal_code"
                name="address.postal_code"
                value={customerInfo.address.postal_code}
                onChange={handleInputChange}
                placeholder="e.g. SW1A 1AA"
              />
            </div>
          </div>
        </div>

        {/* Payment Details with Apple Pay / Google Pay / Card */}
        <div className="payment-element-container">
          <h3>Payment Method</h3>
          <PaymentElement
            options={{
              layout: {
                type: 'tabs',
                defaultCollapsed: false,
              },
              paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'amazon_pay', 'revolut_pay']
            }}
          />
        </div>

        <button
          disabled={isLoading || !stripe || !elements}
          className="payment-submit-button"
          type="submit"
        >
          {isLoading ? (
            <div className="spinner"></div>
          ) : (
            'Complete Payment'
          )}
        </button>

        {message && <div className={`payment-message ${message.includes('successful') ? 'success' : 'error'}`}>{message}</div>}
      </form>
    </div>
  );
};

const CartPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [isCartLoaded, setIsCartLoaded] = useState(false);

  const { away: isOrderingBlocked, backOn } = useSiteStatus();
  const backDate = formatBackDate(backOn);

  // Load cart items from localStorage on component mount
  useEffect(() => {
    const savedCartItems = localStorage.getItem('panelaCrumbsCart');
    if (savedCartItems) {
      const parsedItems = JSON.parse(savedCartItems);
      setCartItems(parsedItems);
    }
    setIsCartLoaded(true);
  }, []);

  // Save cart items to localStorage whenever cartItems changes
  useEffect(() => {
    if (!isCartLoaded) return;
    localStorage.setItem('panelaCrumbsCart', JSON.stringify(cartItems));
    // Dispatch cart update event
    window.dispatchEvent(new Event('cartUpdated'));
  }, [cartItems, isCartLoaded]);

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + (item.priceInCents * item.quantity), 0);
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Handle quantity changes
  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    setCartItems(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  // Remove item from cart
  const removeItem = (itemId) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
  };

  // Proceed to checkout
  const proceedToCheckout = () => {
    if (isOrderingBlocked) {
      alert(`We are not accepting orders right now${backDate ? ` until ${backDate}` : ''}. Please check back then!`);
      return;
    }
    if (cartItems.length === 0) {
      alert('Your cart is empty');
      return;
    }
    setShowCheckout(true);
  };

  // Prepare order for checkout
  const orderForCheckout = {
    id: 'order_' + Date.now(),
    name: `Order (${totalItems} items)`,
    price: `£${(subtotal / 100).toFixed(2)}`,
    priceInCents: subtotal,
    quantity: totalItems,
    items: cartItems.map(item => ({ ...item })),
    // Include personalization data from all items
    isPersonalized: cartItems.some(item => item.isPersonalized),
    personalizationMessage: cartItems.filter(item => item.personalizationMessage).map(item => item.personalizationMessage).join(', '),
    personalizationImages: cartItems.flatMap(item => item.personalizationImages || []),
    collectionDate: cartItems[0]?.collectionDate || 'To be confirmed'
  };

  const handleReturnToMenu = () => {
    setOrderSent(false);
    setLastOrder(null);
    navigate('/menu');
  };

  if (orderSent) {
    const orderItems = lastOrder?.product?.items || [];
    const orderTotal = lastOrder?.product?.price
      || (typeof lastOrder?.product?.priceInCents === 'number'
        ? `£${(lastOrder.product.priceInCents / 100).toFixed(2)}`
        : '£0.00');
    const customerEmail = lastOrder?.customerInfo?.email || lastOrder?.paymentData?.customerInfo?.email;
    const customerName = lastOrder?.customerInfo?.name?.split(' ')?.[0] || lastOrder?.paymentData?.customerInfo?.name?.split(' ')?.[0] || '';
    const paymentId = lastOrder?.paymentData?.paymentIntentId;

    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="order-sent-wrapper">
            <div className="order-confirmation-card">
              <div className="confirmation-icon" aria-hidden="true">🧁</div>
              <h2>Thank you{customerName ? `, ${customerName}` : ''}!</h2>
              <p className="confirmation-message">
                {paymentId ? (
                  <>Your payment has been processed successfully and your order is confirmed! We'll start preparing your delicious treats right away{customerEmail ? ` and send updates to ${customerEmail}` : ''}.</>
                ) : (
                  <>Your order request is on its way to our bakery. We'll be in touch shortly to confirm the details and arrange payment{customerEmail ? ` via ${customerEmail}` : ''}.</>
                )}
              </p>

              <div className="confirmation-summary">
                <div className="confirmation-summary-header">
                  <h3>Order summary</h3>
                  <span className="confirmation-total-amount">{orderTotal}</span>
                </div>

                {orderItems.length > 0 ? (
                  <div className="confirmation-items">
                    {orderItems.map((item, index) => {
                      const itemName = item.productName || item.name || `Item ${index + 1}`;
                      const quantity = item.quantity || 1;
                      const itemTotal = ((item.priceInCents || 0) * quantity) / 100;

                      return (
                        <div className="confirmation-item" key={item.id || index}>
                          <div className="confirmation-item-main">
                            <p className="confirmation-item-name">{itemName}</p>
                            <p className="confirmation-item-meta">
                              Qty {quantity}
                              {item.collectionDate ? ` • Collection: ${item.collectionDate}` : ''}
                            </p>
                          </div>
                          <div className="confirmation-item-price">
                            £{itemTotal.toFixed(2)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="confirmation-note">
                    We’ve received your request and will double-check the item details with you.
                  </p>
                )}
              </div>

              <div className="confirmation-next-steps">
                <h3>What happens next</h3>
                {paymentId ? (
                  <ul>
                    <li>✅ Payment received - Order confirmed!</li>
                    <li>👨‍🍳 We'll start preparing your treats right away.</li>
                    <li>📧 Check your email for order confirmation and collection details.</li>
                    <li>🧁 Your delicious cakes will be ready on the collection date.</li>
                  </ul>
                ) : (
                  <ul>
                    <li>We'll review your order and confirm availability.</li>
                    <li>Our team will reach out to finalise payment and collection.</li>
                    <li>Keep an eye on your inbox for updates from Panela Crumbs.</li>
                  </ul>
                )}
              </div>

              <div className="confirmation-actions">
                <button
                  className="continue-shopping-btn"
                  onClick={handleReturnToMenu}
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <h1>Your Cart</h1>
          <div className="empty-cart">
            <div className="empty-cart-icon">🛒</div>
            <h2>Your cart is empty</h2>
            <p>Add some delicious cakes to get started!</p>
            <button
              className="continue-shopping-btn"
              onClick={() => navigate('/menu')}
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showCheckout) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="checkout-header">
            <button
              className="back-to-cart-btn"
              onClick={() => setShowCheckout(false)}
            >
              ← Back to Cart
            </button>
            <h1>Checkout</h1>
          </div>

          <div className="checkout-layout">
            <div className="order-summary">
              <h3>Order Summary</h3>
              <div className="order-items">
                {cartItems.map(item => (
                  <div key={item.id} className="order-item">
                    <div className="order-item-image">
                      {item.productImage ? (
                        <img src={item.productImage} alt={item.productName} />
                      ) : (
                        <div className="order-item-placeholder">Photo</div>
                      )}
                    </div>
                    <div className="order-item-details">
                      <span className="order-item-name">{item.productName}</span>
                      <span className="order-item-quantity">Qty: {item.quantity}</span>
                      <span className="order-item-collection">Collection: {item.collectionDate}</span>
                      {item.selectedSize && (
                        <span className="order-item-size">
                          Size: {item.selectedSize.dimensions} - {item.selectedSize.portions}
                        </span>
                      )}
                      {item.selectedFlavor && (
                        <span className="order-item-flavor">
                          Flavor: {item.selectedFlavor.name}
                        </span>
                      )}
                      {item.isPersonalized && (
                        <span className="order-item-personalization">
                          Personalised
                          {item.personalizationMessage && ` - "${item.personalizationMessage}"`}
                        </span>
                      )}
                      <span className="order-item-price">£{((item.priceInCents * item.quantity) / 100).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="order-total">
                <strong>Total: £{(subtotal / 100).toFixed(2)}</strong>
              </div>
            </div>

            <div className="payment-section">
              <ErrorBoundary>
                <StripeElementsWrapper
                  product={orderForCheckout}
                  onSuccess={(paymentData) => {
                    console.log('Payment successful:', paymentData);
                    setLastOrder({ product: orderForCheckout, paymentData });
                    setCartItems([]);
                    localStorage.removeItem('panelaCrumbsCart');
                    setOrderSent(true);
                  }}
                  onError={(error) => {
                    console.error('Payment error:', error);
                    alert('There was an error processing your payment. Please try again.');
                  }}
                />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        <div className="cart-header">
          <h1>Your Cart ({totalItems} items)</h1>
          <button
            className="continue-shopping-link"
            onClick={() => navigate('/menu')}
          >
            Continue Shopping
          </button>
        </div>

        <div className="cart-content">
          <div className="cart-items">
            {cartItems.map(item => (
              <div key={item.id} className="cart-item">
                <div className="cart-item-image">
                  {item.productImage ? (
                    <img src={item.productImage} alt={item.productName} />
                  ) : (
                    <div className="cart-item-placeholder">
                      <span>Photo</span>
                    </div>
                  )}
                </div>

                <div className="cart-item-details">
                  <h3 className="cart-item-name">{item.productName}</h3>
                  <p className="cart-item-collection">Collection: {item.collectionDate}</p>
                  {item.selectedSize && (
                    <p className="cart-item-size">
                      Size: {item.selectedSize.dimensions} - {item.selectedSize.portions} ({item.selectedSize.price})
                    </p>
                  )}
                  {item.selectedFlavor && (
                    <p className="cart-item-flavor">
                      Flavor: {item.selectedFlavor.name} ({item.selectedFlavor.price})
                    </p>
                  )}
                  {item.isPersonalized && (
                    <div className="cart-item-personalization">
                      <p className="personalization-label">
                        Personalised
                      </p>
                      {item.personalizationMessage && (
                        <p className="personalization-message">
                          "{item.personalizationMessage}"
                        </p>
                      )}
                      {item.personalizationImages && item.personalizationImages.length > 0 && (
                        <p className="personalization-images-count">
                          {item.personalizationImages.length} reference image{item.personalizationImages.length > 1 ? 's' : ''} attached
                        </p>
                      )}
                    </div>
                  )}
                  <p className="cart-item-price">£{(item.priceInCents / 100).toFixed(2)} each</p>
                </div>

                <div className="cart-item-controls">
                  <div className="quantity-controls">
                    <button
                      className="quantity-btn"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      −
                    </button>
                    <span className="quantity-display">{item.quantity}</span>
                    <button
                      className="quantity-btn"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>

                  <div className="item-total">
                    £{((item.priceInCents * item.quantity) / 100).toFixed(2)}
                  </div>

                  <button
                    className="remove-btn"
                    onClick={() => removeItem(item.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <div className="cart-summary-content">
              <h3>Order Summary</h3>
              <div className="summary-row">
                <span>Subtotal ({totalItems} items)</span>
                <span>£{(subtotal / 100).toFixed(2)}</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>£{(subtotal / 100).toFixed(2)}</span>
              </div>
              {isOrderingBlocked ? (
                <div className="ordering-blocked-notice">
                  <span className="ordering-blocked-icon">🚫</span>
                  <p>We are not accepting orders{backDate ? <> until <strong>{backDate}</strong></> : ' right now'}.</p>
                  <p>Please check back then — we can't wait to bake for you!</p>
                </div>
              ) : (
                <button
                  className="checkout-btn"
                  onClick={proceedToCheckout}
                >
                  Proceed to Checkout
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
